import {
    useTheme
} from "@fluentui/react";
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import FullCalendar from "@fullcalendar/react";
import { EventApi, EventDropArg, DateSpanApi } from "@fullcalendar/core"
import { EventReceiveArg } from "@fullcalendar/interaction"

import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import rrulePlugin from '@fullcalendar/rrule'
import { add, format, startOfWeek, sub } from 'date-fns'
import './assets/scheduler.scss'
import { resourcesContext } from "../../providers/resources";
import interactionPlugin from "@fullcalendar/interaction"
import { EventRender, OtherSiteEventRender } from "./Events/EventRender";
import { DraftStatus, shiftsContext } from "../../providers/shifts";
import { Columns } from "./Columns/Columns";
import { eventAllowed, eventDragStop } from "../../helpers/calendar";
import { Header } from "./Header/Header";
import Legend from './Legend/Legend';
import { renderGenericFallbackUi } from '../error-boundary/generic-fallback-ui';
import { IEvent } from "../../interfaces/event";
import { AppInsightsErrorBoundary } from "@microsoft/applicationinsights-react-js";
import { reactPlugin } from '../../applicationInsights';
import Swal from 'sweetalert2';
import { toast } from '../../helpers/notifications';
import { createLoadDraftPromptOptions } from '../../helpers/drafts/globalDraftAvailability';


export type TView = "resourceTimelineWeek" | "resourceTimelineFortnight"
const Scheduler = ({ openModal }: { openModal: (id: string) => void }) => {
    const { getAllResources, getIlluminaAvailablities } = useContext(resourcesContext)
    const {
        moveShift,
        getShiftsForCalendarView,
        getOtherSiteShiftsForCalendarView,
        loader,
        checkShiftLocationIsValid,
        checkShiftUnsavedChanges,
        markAllShiftsAsUnchanged,
        saveDraft,
        setCalendarDates,
        getShift,
        checkIsModified,
        draftStatus,
        getResourceData,
        checkRequiredCharacteristics,
        defineUserAvailability,
        checkIfShiftAllowed,
        startDate,
        endDate,
        showOtherSiteShifts,
        toggleShowOtherSiteShifts,
        loadDraft,
        view,
        setView,
        loadingPayrollData,
        isCancellationReviewPending,
        validated,
        currentPayrollRange,
        getPayrollData,
        setRosterboardDragActive,
        isGlobalDraftEnabled
    } = useContext(shiftsContext)

    const theme = useTheme();
    const [loadingBidders, setLoadingBidders] = useState(false);
    // Count how many requests are in flight.
    const [inFlightMoveCount, setInFlightMoveCount] = useState(0)
    // Short cooldown period - prevents rapid drags from fighting each other.
    const [isDndCooldown, setIsDndCooldown] = useState(false)
    const dndCooldownTimerRef = useRef<number | null>(null)
    const lastBusyToastAtRef = useRef<number>(0)
    const calendarRef = useRef<FullCalendar | null>(null);
    const [layoutVersion, setLayoutVersion] = useState(0);
    // Cache the columns so we don't rebuild them on every render.
    const resourceAreaColumns = useMemo(
        () => Columns({ getResourceData, showOtherSiteShifts }),
        [getResourceData, showOtherSiteShifts]
    );

    // Signal downstream renders (e.g., contracted-hours) to recompute when calendar layout/data shifts.
    const bumpLayoutVersion = React.useCallback(() => {
        setLayoutVersion((v) => v + 1);
    }, []);
    
    // Wrap gotoDate so every navigation also bumps the layoutVersion signal.
    const gotoDateAndSignal = React.useCallback((date: Date) => {
        calendarRef.current?.getApi().gotoDate(date)
        bumpLayoutVersion();
    }, [bumpLayoutVersion]);

    useEffect(() => {
        if (draftStatus === DraftStatus.found && validated) {
            handleShowLoadDraftPrompt()
        }
    }, [draftStatus, validated])


    // Build the event list for the visible date range.
    const displayedEvents = useMemo(() => {
        const getShift = getShiftsForCalendarView();
        const availablities = getIlluminaAvailablities();
        const getOtherAvailabilities = availablities.filter(item => item.display !== 'contractedHours');

        const eventData = [
            ...getOtherAvailabilities,
            ...(showOtherSiteShifts ? getOtherSiteShiftsForCalendarView() : []),
            ...getShift
        ]

        const getContractedHours = availablities.filter(item => {
            if (item.display  !== 'contractedHours') {
                return false;
            }

            const itemStart = new Date(item.start);
            const itemEnd = new Date(item.end);

            const hasDateOverlap = (
                (itemStart <= endDate && itemEnd >= startDate)
            );

            return hasDateOverlap;
        });

        const newEventData = eventData.concat(getContractedHours);

        // const resources = getAllResources() ?? [];
        // const currentDate = new Date(startDate);

        // resources.forEach(resource => {
        //     let currentDay = new Date(currentDate);
        //     while (currentDay <= endDate) {
        //         const hasEvent = filteredContractedHours.some(event => 
        //             (event as IEvent).resourceId === resource.id && 
        //             isSameDay(new Date(event.start), currentDay)
        //         );

        //         if (!hasEvent) {
        //             const resourceEvents = eventData.filter(event => 
        //                 (event as IEvent).resourceId === resource.id
        //             );
                    
        //             const hasUnavailableEvent = resourceEvents.some(event => {
        //                 if (event.display?.toLowerCase() !== 'unavailable') {
        //                     return false;
        //                 }
                        
        //                 const eventStart = new Date(event.start);
        //                 const eventEnd = new Date(event.end);
                        
        //                 const currentDayStart = startOfDay(currentDay);
        //                 const eventStartDay = startOfDay(eventStart);
        //                 const eventEndDay = startOfDay(eventEnd);
                        
        //                 return (eventStartDay <= currentDayStart && eventEndDay >= currentDayStart);
        //             });

        //             if (!hasUnavailableEvent) {
        //                 let event = {
        //                     resourceId: resource.id,
        //                     title: `noContractedHours`,
        //                     id: `empty_${resource.id}_${format(currentDay, 'yyyy-MM-dd')}`,
        //                     display: 'noContractedHours',
        //                     description: 'error',
        //                     editable: true,
        //                     start: subMinutes(endOfDay(currentDay), 1),
        //                     end: endOfDay(currentDay),
        //                     resourceEditable: true,
        //                 }  as unknown as IEvent;
        //                 newEventData.push(event);
        //             }
        //         }
                
        //         currentDay.setDate(currentDay.getDate() + 1);
        //     }
        // });
        // newEventData.push({
        //     id: 'marker',
        //     start: 0,
        //     display: 'background'
        // } as unknown as IEvent)
        return newEventData as IEvent[];
    }, [
        endDate,
        getIlluminaAvailablities,
        getOtherSiteShiftsForCalendarView,
        getShiftsForCalendarView,
        showOtherSiteShifts,
        startDate,
    ])

    // Keep a ref copy so drag handlers can read without re-rendering.
    const displayedEventsRef = useRef<any[]>([])
    useEffect(() => {
        displayedEventsRef.current = displayedEvents as any[]
    }, [displayedEvents])

    // When dragging, we can "freeze" the UI list to avoid jitter.
    const [isCalendarUiFrozen, setIsCalendarUiFrozen] = useState(false)
    const frozenEventsRef = useRef<any[]>([])
    const [calendarEventsState, setCalendarEventsState] = useState<IEvent[]>([])
    const calendarEventsRef = useRef<IEvent[]>([])
    // Only these fields require a full event rebuild in FullCalendar.
    const structuralKeys = useMemo(() => new Set(["id", "start", "end", "resourceId", "allDay", "display"]), [])

    // Decide if we need to rebuild the event list or can do small updates.
    const hasStructuralChanges = React.useCallback(
        (prevEvents: IEvent[], nextEvents: IEvent[]) => {
            if (prevEvents.length !== nextEvents.length) return true;
            const prevMap = new Map<string, IEvent>();
            prevEvents.forEach((ev) => prevMap.set(ev.id, ev));
            if (prevMap.size !== nextEvents.length) return true;
            for (const next of nextEvents) {
                const prev = prevMap.get(next.id);
                if (!prev) return true;
                const prevStart = prev.start ? new Date(prev.start).getTime() : null;
                const nextStart = next.start ? new Date(next.start).getTime() : null;
                const prevEnd = prev.end ? new Date(prev.end).getTime() : null;
                const nextEnd = next.end ? new Date(next.end).getTime() : null;
                if (prevStart !== nextStart || prevEnd !== nextEnd) return true;
                if ((prev as any).resourceId !== (next as any).resourceId) return true;
                if ((prev as any).display !== (next as any).display) return true;
                if ((prev as any).allDay !== (next as any).allDay) return true;
            }
            return false;
        },
        []
    );

    // Update only titles/extended props so we avoid rebuilding events and triggering visual jumps.
    const applyMinorEventUpdates = React.useCallback(
        (prevEvents: IEvent[], nextEvents: IEvent[]) => {
            const api = calendarRef.current?.getApi();
            if (!api) return false;
            const prevMap = new Map<string, IEvent>();
            prevEvents.forEach((ev) => prevMap.set(ev.id, ev));
            // The FullCalendar API exposes batchRendering even though the typings omit it.
            (api as any).batchRendering(() => {
                nextEvents.forEach((next) => {
                    const prev = prevMap.get(next.id);
                    if (!prev) return;
                    const calEvent = api.getEventById(next.id);
                    if (!calEvent) return;
                    if (prev.title !== next.title && next.title != null) {
                        calEvent.setProp("title", next.title);
                    }
                    Object.keys(next).forEach((key) => {
                        if (structuralKeys.has(key) || key === "title") return;
                        const prevVal = (prev as any)[key];
                        const nextVal = (next as any)[key];
                        if (prevVal !== nextVal) {
                            calEvent.setExtendedProp(key, nextVal);
                        }
                    });
                });
            });
            bumpLayoutVersion();
            return true;
        },
        [bumpLayoutVersion, structuralKeys]
    );

    // Keep the FullCalendar event list in sync with minimal churn.
    const syncCalendarEvents = React.useCallback(
        (nextEvents: IEvent[]) => {
            const prevEvents = calendarEventsRef.current;
            if (!prevEvents.length) {
                setCalendarEventsState(nextEvents);
                calendarEventsRef.current = nextEvents;
                return;
            }
            if (hasStructuralChanges(prevEvents, nextEvents)) {
                setCalendarEventsState(nextEvents);
                calendarEventsRef.current = nextEvents;
                return;
            }
            const applied = applyMinorEventUpdates(prevEvents, nextEvents);
            if (applied) {
                calendarEventsRef.current = nextEvents;
            } else {
                setCalendarEventsState(nextEvents);
                calendarEventsRef.current = nextEvents;
            }
        },
        [applyMinorEventUpdates, hasStructuralChanges]
    );

    useEffect(() => {
        // Freeze the list while dragging to avoid UI jumps.
        if (isCalendarUiFrozen) {
            const frozen = frozenEventsRef.current as IEvent[];
            setCalendarEventsState(frozen);
            calendarEventsRef.current = frozen;
            return;
        }
        syncCalendarEvents(displayedEvents as IEvent[]);
    }, [displayedEvents, isCalendarUiFrozen, syncCalendarEvents]);

    // Memoize events and resources to avoid unnecessary re-renders
    const memoizedEvents = useMemo(() => calendarEventsState, [calendarEventsState])
    const memoizedResources = useMemo(() => getAllResources() ?? [], [getAllResources, showOtherSiteShifts])
    // Signal downstream renderers (contracted-hours arrows) when data/layout context changes.
    useEffect(() => {
        bumpLayoutVersion();
    }, [calendarEventsState, view, startDate, endDate, bumpLayoutVersion]);

    const handleShowLoadDraftPrompt = async () => {
        const result = await Swal.fire(createLoadDraftPromptOptions(isGlobalDraftEnabled))

        if (result.isConfirmed) {
            loadDraft();
        }
    }

    const handleShowUnsavedChangesPrompt = async () => {
        return await Swal.fire({
            title: 'Unsaved Changes',
            text: 'You have unsaved changes. Click "Yes" to save draft before navigating, or "No" to navigate without saving.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            cancelButtonColor: theme.palette.tealLight,
            confirmButtonColor: theme.palette.themePrimary,
        });
    }

    const confirmUnsavedNavigation = async (): Promise<boolean> => {
        if (!checkShiftUnsavedChanges()) return true;

        const promptResult = await handleShowUnsavedChangesPrompt();
        // "No" is the cancel button in this dialog and should continue navigation without saving.
        if (promptResult.dismiss === Swal.DismissReason.cancel) {
            return true;
        }
        if (promptResult.isDismissed && !promptResult.isDenied && !promptResult.isConfirmed) {
            return false;
        }

        if (promptResult.isConfirmed) {
            const saved = await saveDraft();
            if (!saved) {
                toast('error', 'Draft could not be saved. Your changes are still on the roster. Try again or use "No" to navigate without saving.');
                return false;
            }
        }

        return true;
    }

    const views = {
        resourceTimelineWeek: {
            type: 'resourceTimeline',
            buttonText: 'Week',
            slotDuration: { day: 1 },
            duration: { days: 7 },
        },
        resourceTimelineFortnight: {
            type: 'resourceTimeline',
            slotDuration: { day: 1 },
            buttonText: 'Fortnight',
            duration: { days: 14 },
        }
    }

    const setCalendarDatesWithView = (date: Date) => {
        if (loadingPayrollData || checkShiftUnsavedChanges()) return;
        setCalendarDates(date, view)
    }

    const updateDateRange = async (dates: Date[]) => {
        if (loadingPayrollData) return;
        if (!(await confirmUnsavedNavigation())) return;
        if (!dates || dates.length === 0) return;

        markAllShiftsAsUnchanged();
        // Resolve payroll range for the clicked date and jump to that range if found.
        const result = await getPayrollData(dates[0], view);
        if (result?.startForView && result?.endForView) {
            gotoDateAndSignal(result.startForView)
            console.info(`Selected payroll cycle ${format(result.startForView, 'd/M/yyyy')} - ${format(result.endForView, 'd/M/yyyy')}`)
            return
        }

        if (view === "resourceTimelineWeek") {
            const startDate = startOfWeek(dates[0], { weekStartsOn: 1 });
            setCalendarDatesWithView(startDate)
            gotoDateAndSignal(startDate)
        } else {
            setCalendarDatesWithView(dates[0])
            gotoDateAndSignal(dates[0])
        }
    }

    const increaseDate = async () => {
        if (!(await confirmUnsavedNavigation())) return;
        markAllShiftsAsUnchanged();
        const nDate = add(startDate, {
            weeks: view === "resourceTimelineWeek" ? 1 : 2
        })
        // Prefer payroll-resolved range; fall back to week-snap only if no cycle matched.
        const result = await getPayrollData(nDate, view);
        if (result?.startForView) {
            gotoDateAndSignal(result.startForView)
            return
        }
        const fallbackDate = view === "resourceTimelineWeek" ? startOfWeek(nDate, { weekStartsOn: 1 }) : nDate;
        setCalendarDatesWithView(fallbackDate)
        gotoDateAndSignal(fallbackDate)
    }

    const decreaseDate = async () => {
        if (!(await confirmUnsavedNavigation())) return;
        markAllShiftsAsUnchanged();
        const nDate = sub(startDate, {
            weeks: view === "resourceTimelineWeek" ? 1 : 2
        })
        // Prefer payroll-resolved range; fall back to week-snap only if no cycle matched.
        const result = await getPayrollData(nDate, view);
        if (result?.startForView) {
            gotoDateAndSignal(result.startForView)
            return
        }
        const fallbackDate = view === "resourceTimelineWeek" ? startOfWeek(nDate, { weekStartsOn: 1 }) : nDate;
        setCalendarDatesWithView(fallbackDate)
        gotoDateAndSignal(fallbackDate)
    }

    const setToCurrentWeek = async () => {
        if (!(await confirmUnsavedNavigation())) return;
        markAllShiftsAsUnchanged();
        // Jump to the correct payroll cycle for "today", with a week-view fallback.
        const today = new Date();
        const result = await getPayrollData(today, view);
        if (result?.startForView) {
            gotoDateAndSignal(result.startForView)
            return
        }
        if (view === "resourceTimelineWeek") {
            const fallbackDate = startOfWeek(today, { weekStartsOn: 1 });
            gotoDateAndSignal(fallbackDate)
            setCalendarDatesWithView(fallbackDate)
        }
    }

    const updateView = async (v: TView) => {
        setView(v)
        calendarRef.current?.getApi().changeView(v)
        bumpLayoutVersion();
        // When switching views, prefer the payroll-resolved range for the new view.
        const result = await getPayrollData(startDate, v);
        if (result?.startForView) {
            gotoDateAndSignal(result.startForView)
            return
        }
        if (v === "resourceTimelineWeek") {
            const fallbackDate = startOfWeek(startDate, { weekStartsOn: 1 });
            setCalendarDates(fallbackDate, v)
            gotoDateAndSignal(fallbackDate)
        }
    }

    const eventClick = async (event: EventApi) => {
        if (
            event.display == 'background' ||
            event.display === 'unavailable' ||
            event.extendedProps?.availabilityType != null
        ) {
            return;
        }

        if (!event.extendedProps.otherSite) {
            const eventRequested = getShift(event.id);
            if (eventRequested?.bidders?.length == 0) {
                openModal(event.id);
                return;
            }

            setLoadingBidders(true);

            await checkIfShiftAllowed(eventRequested as IEvent);

            openModal(event.id);
            setLoadingBidders(false);
        }
    }

    const openNewJobModal = () => {
        window.top?.postMessage({ type: 'ac_rb_new_job' }, '*');
    }

    // Combine any "global" loading states into a single overlay.
    const isBlockingLoading = loader?.loading || draftStatus === DraftStatus.publishing;
    const blockingMessage = draftStatus === DraftStatus.publishing ? "Publishing..." : (loader?.message ?? "Loading...");

    // Short delay to keep rapid drop/receive events from colliding.
    const startDndCooldown = () => {
        setIsDndCooldown(true)
        if (dndCooldownTimerRef.current != null) {
            window.clearTimeout(dndCooldownTimerRef.current)
        }
        dndCooldownTimerRef.current = window.setTimeout(() => {
            dndCooldownTimerRef.current = null
            setIsDndCooldown(false)
        }, 300)
    }

    useEffect(() => {
        // Clean up any pending cooldown timers on unmount.
        return () => {
            if (dndCooldownTimerRef.current != null) {
                window.clearTimeout(dndCooldownTimerRef.current)
                dndCooldownTimerRef.current = null
            }
        }
    }, [])

    // Local visual cue for drag state.
    const [dragIndicator, setDragIndicator] = useState<{ isActive: boolean, label: string }>({
        isActive: false,
        label: ''
    })
    const dragIndicatorElRef = useRef<HTMLDivElement | null>(null)
    const dragIndicatorPosRef = useRef<{ x: number, y: number }>({ x: 0, y: 0 })
    const dragIndicatorRafRef = useRef<number | null>(null)
    const dragStartTokenRef = useRef(0)
    const showDragIndicator = false



    // NOTE: we intentionally do not programmatically restore `.fc-scroller` positions here.
    // It can cause scroll-sync feedback loops (horizontal + vertical oscillation) in ScrollGrid.

    // Throttle pointermove updates so we don't overload the UI thread.
    useEffect(() => {
        if (!showDragIndicator || !dragIndicator.isActive) return

        let lastFrame = 0;
        const onPointerMove = (event: PointerEvent) => {
            const now = performance.now();
            if (now - lastFrame < 16) return; // ~60fps throttle
            lastFrame = now;
            if (dragIndicatorRafRef.current != null) return;
            dragIndicatorRafRef.current = requestAnimationFrame(() => {
                dragIndicatorRafRef.current = null;
                dragIndicatorPosRef.current = { x: event.clientX, y: event.clientY };
                const el = dragIndicatorElRef.current;
                if (el) {
                    el.style.transform = `translate3d(${event.clientX + 12}px, ${event.clientY + 12}px, 0)`;
                }
            });
        };

        window.addEventListener('pointermove', onPointerMove, true);

        const el = dragIndicatorElRef.current;
        if (el) {
            const { x, y } = dragIndicatorPosRef.current;
            el.style.transform = `translate3d(${x + 12}px, ${y + 12}px, 0)`;
        }
        return () => {
            window.removeEventListener('pointermove', onPointerMove, true);
            if (dragIndicatorRafRef.current != null) {
                cancelAnimationFrame(dragIndicatorRafRef.current);
                dragIndicatorRafRef.current = null;
            }
        };
    }, [dragIndicator.isActive]);

    function handleEventDrop(info: EventDropArg) {
        // If we're already processing a drop, block this one to avoid conflicts.
        if (isDndCooldown) {
            const now = Date.now();
            if (now - lastBusyToastAtRef.current > 1500) {
                lastBusyToastAtRef.current = now;
                toast('warning', 'Please wait a moment');
            }
            (info as any).revert?.();
            setIsCalendarUiFrozen(false);
            setRosterboardDragActive(false);
            setDragIndicator((prev) => ({ ...prev, isActive: false }));
            return;
        }
        if (info.event.extendedProps?.loading || info.event.extendedProps?.validating) {
            setIsCalendarUiFrozen(false);
            setRosterboardDragActive(false);
            setDragIndicator((prev) => ({ ...prev, isActive: false }));
            return;
        }

        // Start the move and keep UI state consistent during the async call.
        startDndCooldown();
        setInFlightMoveCount((c) => c + 1);
        moveShift(info.event.id, info.newResource?.id)
            .then((success) => {
                if (!success) {
                    (info as any).revert?.();
                }
            })
            .catch(() => {
                // Always unfreeze UI on error
                setIsCalendarUiFrozen(false);
                setRosterboardDragActive(false);
                setDragIndicator((prev) => ({ ...prev, isActive: false }));
            })
            .finally(() => {
                setIsCalendarUiFrozen(false);
                setRosterboardDragActive(false);
                setDragIndicator((prev) => ({ ...prev, isActive: false }));
                setInFlightMoveCount((c) => Math.max(0, c - 1));
            });
    }

    function handleEventReceive(info: EventReceiveArg) {
        // Same guard as drop: avoid overlapping operations.
        if (isDndCooldown) {
            const now = Date.now();
            if (now - lastBusyToastAtRef.current > 1500) {
                lastBusyToastAtRef.current = now;
                toast('warning', 'Please wait a moment');
            }
            (info as any).revert?.();
            setIsCalendarUiFrozen(false);
            setRosterboardDragActive(false);
            setDragIndicator((prev) => ({ ...prev, isActive: false }));
            return;
        }
        if (info.event.extendedProps?.loading || info.event.extendedProps?.validating) {
            setIsCalendarUiFrozen(false);
            setRosterboardDragActive(false);
            setDragIndicator((prev) => ({ ...prev, isActive: false }));
            return;
        }
        const resources = info.event.getResources();
        if (Array.isArray(resources) && resources.length) {
            // Track the async move so the header can show a small counter.
            startDndCooldown();
            setInFlightMoveCount((c) => c + 1);
            moveShift(info.event.id, resources[0].id)
                .then((success) => {
                    if (!success) {
                        (info as any).revert?.();
                    }
                })
                .catch(() => {
                    setIsCalendarUiFrozen(false);
                    setRosterboardDragActive(false);
                    setDragIndicator((prev) => ({ ...prev, isActive: false }));
                })
                .finally(() => {
                    setIsCalendarUiFrozen(false);
                    setRosterboardDragActive(false);
                    setDragIndicator((prev) => ({ ...prev, isActive: false }));
                    setInFlightMoveCount((c) => Math.max(0, c - 1));
                });
        }
    }

    function handleEventAllow(span: DateSpanApi, event: EventApi | null): boolean {
        if (event?.extendedProps.loading || event?.extendedProps?.validating) {
            return false
        }
        return eventAllowed(span, checkShiftLocationIsValid, calendarRef, event, defineUserAvailability)
    }
    

    return (
        <div>
            {loadingBidders &&
                <div className='loading style-2'>
                    <div className='loading-wheel' />
                    <div className='loading-text'>Loading...</div>
                </div>
            }

                <div style={{ boxSizing: 'border-box', display: 'flex', flexDirection: 'column', height: '100vh' }}>
                <Header
                    increaseDate={increaseDate}
                    decreaseDate={decreaseDate}
                    setToCurrentWeek={setToCurrentWeek}
                    date={startDate}
                    updateDateRange={updateDateRange}
                    dateRange={[startDate, endDate]}
                    updateView={updateView}
                    view={view}
                    setDate={setCalendarDatesWithView}
                    showOtherSiteShifts={showOtherSiteShifts}
                    toggleShowOtherSitesShifts={toggleShowOtherSiteShifts}
                    loadingPayrollData={loadingPayrollData}
                    openNewJobModal={openNewJobModal}
                    inFlightCount={inFlightMoveCount}
                    payrollRange={currentPayrollRange}
                />
                <div
                    style={{ display: 'flex', flex: 1 }}
                    id={"calender"}
                    className={isDndCooldown ? 'rb-dnd-locked' : undefined}
                >
                    <div style={{ flex: 6 }}>
                        <AppInsightsErrorBoundary
                            onError={() =>
                                renderGenericFallbackUi({
                                    mainHeading: 'Something went wrong whilst trying to load the scheduler',
                                    subHeading: 'Please try again in a few minutes'
                                })
                            }
                            appInsights={reactPlugin}
                        >
                            {/* FullCalendar is vendor code. See `patches/@fullcalendar+interaction+6.1.15.patch` + README for our local fix. */}
                            <FullCalendar
                                ref={calendarRef}
                                resourceAreaColumns={resourceAreaColumns}
                                plugins={[resourceTimelinePlugin, interactionPlugin, rrulePlugin]}
                                headerToolbar={false}
                                // Keep scrolling predictable while dragging.
                                dragScroll={false}
                                scrollTime={null}
                                scrollTimeReset={false}
                                nowIndicator={false}
                                stickyHeaderDates={false}
                                stickyFooterScrollbar={false}
                                eventDragMinDistance={5}
                                slotMinWidth={180}
                                height={'100%'}
                                initialDate={startDate}
                                eventDragStart={React.useCallback((info) => {
                                    // Capture details early so we can keep the drag UI stable.
                                    const dragStartToken = ++dragStartTokenRef.current;
                                    // If we're cooling down, block the drag and keep UI calm.
                                    if (isDndCooldown) {
                                        const now = Date.now();
                                        if (now - lastBusyToastAtRef.current > 1500) {
                                            lastBusyToastAtRef.current = now;
                                            toast('warning', 'Please wait a moment');
                                        }
                                        setIsCalendarUiFrozen(false);
                                        setRosterboardDragActive(false);
                                        setDragIndicator((prev) => ({ ...prev, isActive: false }));
                                        return;
                                    }
                                    // Pull pointer info so we can position the drag label.
                                    const jsEvent = (info as any).jsEvent as MouseEvent | PointerEvent | undefined;
                                    const clientX = jsEvent?.clientX;
                                    const clientY = jsEvent?.clientY;
                                    const dragLabel = info.event?.title ? info.event.title : '';

                                    // Freeze the event list during drag to avoid jumpy re-renders.
                                    frozenEventsRef.current = calendarEventsRef.current;
                                    requestAnimationFrame(() => {
                                        if (dragStartTokenRef.current !== dragStartToken) {
                                            return;
                                        }
                                        setIsCalendarUiFrozen(true);
                                        setRosterboardDragActive(true);

                                        // Keep the drag indicator near the pointer.
                                        if (clientX != null && clientY != null) {
                                            dragIndicatorPosRef.current = { x: clientX, y: clientY };
                                            if (dragIndicatorElRef.current) {
                                                dragIndicatorElRef.current.style.transform = `translate3d(${clientX + 12}px, ${clientY + 12}px, 0)`;
                                            }
                                        }

                                        // Show a small label so users know what they grabbed.
                                        setDragIndicator({
                                            isActive: true,
                                            label: dragLabel,
                                        });
                                    });
                                }, [isDndCooldown])}
                                eventDragStop={React.useCallback((info) => {
                                    // Invalidate any in-flight drag work and reset state.
                                    dragStartTokenRef.current += 1;
                                    setIsCalendarUiFrozen(false);
                                    setRosterboardDragActive(false);
                                    setDragIndicator((prev) => ({ ...prev, isActive: false }));
                                    eventDragStop(calendarRef);
                                }, [])}
                                // Subtle styling for days in the past.
                                slotLaneClassNames={React.useCallback((arg: any) => {
                                    const cellDate = new Date(arg.date);
                                    cellDate.setHours(0, 0, 0, 0);
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0);
                                    return cellDate < today ? ['rb-past-slot'] : [];
                                }, [])}
                                slotLabelContent={React.useCallback((e) => {
                                    const date = new Date(e.date);
                                    return (<>{format(date, 'EEEE do')}</>);
                                }, [])}
                                slotLabelFormat={{ weekday: 'long', day: 'numeric' }}
                                schedulerLicenseKey={'CC-Attribution-NonCommercial-NoDerivatives'}
                                views={views}
                                // datesSet={(dateRange) => {
                                //     setCalendarDates(dateRange.start, view)
                                // }}
                                initialView={view}
                                resources={memoizedResources}
                                events={memoizedEvents}
                                eventContent={React.useCallback(({ event, isDragging }) => {
                                    if (event.extendedProps.otherSite) {
                                        return (<OtherSiteEventRender event={event} />);
                                    } else {
                                        return (
                                            <EventRender
                                                event={event}
                                                getShift={getShift}
                                                isDragging={isDragging}
                                                checkCharacteristics={checkRequiredCharacteristics}
                                                getResourceData={getResourceData}
                                                checkIsModified={checkIsModified}
                                                eventClick={eventClick}
                                                isCancellationReviewPending={isCancellationReviewPending}
                                                layoutVersion={layoutVersion}
                                            />
                                        );
                                    }
                                }, [getShift, checkRequiredCharacteristics, getResourceData, checkIsModified, eventClick, isCancellationReviewPending, layoutVersion])}
                                eventColor={'transparent'}
                                eventTextColor={'black'}
                                editable={false}
                                eventDurationEditable={false}
                                eventResourceEditable={!isDndCooldown}
                                eventClick={(info) => {
                                    eventClick(info.event);
                                }}
                                resourceOrder={'index'}
                                resourceAreaWidth={350}
                                eventOverlap={true}
                                eventAllow={handleEventAllow}
                                eventDrop={handleEventDrop}
                                eventReceive={handleEventReceive}
                            />
                        </AppInsightsErrorBoundary>
                    </div>
                </div>

                <Legend />
            </div>

            <div
                ref={dragIndicatorElRef}
                className="rb-drag-indicator"
                style={{ display: showDragIndicator && dragIndicator.isActive ? 'block' : 'none' }}
            >
                {dragIndicator.label}
            </div>

            {isBlockingLoading && (
                // Simple modal overlay to prevent edits while data is busy.
                <div className="rb-blocking-overlay" aria-live="polite" aria-busy="true">
                    <div className="rb-blocking-modal" role="dialog" aria-modal="true" aria-label={blockingMessage}>
                        <div className="rb-blocking-wheel" />
                        <div className="rb-blocking-text">{blockingMessage}</div>
                    </div>
                </div>
            )}
        </div>
    )
}


export { Scheduler }
